import { useDeliveryApi } from 'infrastructure/api';
import { useRef } from 'react';
import { ResourceData, useCrudService } from 'framework/crud';
import {
  cancelTaskValidation,
  createClientPortalTaskValidation,
} from './portalTasks/createUpdate/createClientPortalTaskValidation';
import { rejectClientUsersValidation } from './portalClients/inviteClientUserValidation';
import { ClientPortalRedmineRequest, ClientPortalRedmineRequestsResponse, ClientPortalTaskAttachment } from './types';

type ActiveUserAccessRequestsResponse = {
  result?: ClientPortalRedmineRequest[];
};

type ClientPortalCommentPayload = {
  text: string;
};

const useClientPortalService = () => {
  const singleResourceUrl = '/projects/:id';
  const {
    search: baseSearch,
    update: initialUpdate,
    ...baseCrudRequests
  } = useCrudService({
    singleResourceUrl,
    listResourceUrl: '/projects',
    useService: useDeliveryApi,
  });

  const { sendGetRequest, sendPostRequest, sendPatchRequest, sendDeleteRequest } = useDeliveryApi();
  const savedAttachments = useRef(new Map<string, ClientPortalTaskAttachment[]>());
  const createdTask = useRef<ResourceData | null>(null);

  const saveTaskAttachments = async (
    projectId: string,
    taskId: string,
    attachmentIds: string[],
    initialAttachments: ClientPortalTaskAttachment[] = [],
  ) => {
    const key = `${projectId}/${taskId}`;
    let attachments = savedAttachments.current.get(key) ?? initialAttachments;
    const url = `/projects/${projectId}/client-portal/tasks/${taskId}/attachments`;
    const newIds = attachmentIds.filter(id => !attachments.some(attachment => attachment.id === id));
    if (newIds.length) {
      const response = await sendPostRequest(`${url}/commit`, { attachmentIds: newIds });
      const committed: ClientPortalTaskAttachment[] = await response.json();
      if (!Array.isArray(committed) || newIds.some(id => !committed.some(attachment => attachment.id === id))) {
        throw new Error('Failed to commit task attachments');
      }
      attachments = [...attachments, ...committed.filter(item => !attachments.some(old => old.id === item.id))];
      savedAttachments.current.set(key, attachments);
    }
    for (const attachment of attachments) {
      if (!attachmentIds.includes(attachment.id)) {
        await sendDeleteRequest(`${url}/${attachment.id}`);
        attachments = attachments.filter(item => item.id !== attachment.id);
        savedAttachments.current.set(key, attachments);
      }
    }
    savedAttachments.current.delete(key);
    return attachments;
  };

  const createClientPortalTask = async (id: string, data: ResourceData): Promise<ResourceData> => {
    const { attachmentIds = [], attachments, responseAttachments, ...payload } = data;
    const res = createdTask.current
      ? await sendPatchRequest(`/projects/${id}/client-portal/tasks/${createdTask.current.id}`, payload)
      : await sendPostRequest(`/projects/${id}/client-portal/tasks`, payload);
    const task = await res.json();
    createdTask.current = task;
    const committed = await saveTaskAttachments(id, task.id, attachmentIds);
    createdTask.current = null;
    return { ...task, attachments: committed };
  };

  const updateClientPortalTask = async (
    id: string,
    taskId: string,
    data: ResourceData,
    initialAttachments: ClientPortalTaskAttachment[] = data.attachments || [],
  ): Promise<ResourceData> => {
    const { attachmentIds, attachments, responseAttachments, ...payload } = data;
    const res = await sendPatchRequest(`/projects/${id}/client-portal/tasks/${taskId}`, payload);
    const task = await res.json();
    const committed = await saveTaskAttachments(
      id,
      taskId,
      attachmentIds ?? initialAttachments.map(attachment => attachment.id),
      initialAttachments,
    );
    return { ...task, attachments: committed };
  };

  const cancelClientPortalTask = async (id: string, taskId: string, data: ResourceData): Promise<ResourceData> => {
    const res = await sendPostRequest(`/projects/${id}/client-portal/tasks/${taskId}/cancel`, data);
    return res.json();
  };

  const getActivePortalRequests = async (id: string): Promise<ClientPortalRedmineRequestsResponse> => {
    const res = await sendGetRequest(`/projects/${id}/client-portal/user-access-requests`);
    return res.json();
  };

  const inviteClientUsers = async (id: string, data: ResourceData): Promise<ResourceData> => {
    const res = await sendPostRequest(`/projects/${id}/client-portal/user-access-requests`, data);
    return res.json();
  };

  const getActiveUserAccessRequests = async (id: string): Promise<ClientPortalRedmineRequest[]> => {
    const res = await sendGetRequest(`/projects/${id}/client-portal/user-access-requests`);
    const data = (await res.json()) as ActiveUserAccessRequestsResponse;

    return data.result || [];
  };

  const approveClientUsers = async (id: string, requestId: string, data: ResourceData): Promise<ResourceData> => {
    const res = await sendPostRequest(`/projects/${id}/client-portal/user-access-requests/${requestId}/approve`, data);
    return res.json();
  };

  const rejectClientUsers = async (id: string, requestId: string, data: ResourceData): Promise<ResourceData> => {
    const res = await sendPostRequest(`/projects/${id}/client-portal/user-access-requests/${requestId}/reject`, data);
    return res.json();
  };

  const createClientPortalTaskComment = async (
    projectId: string,
    taskId: string,
    data: ClientPortalCommentPayload,
  ): Promise<ResourceData> => {
    const res = await sendPostRequest(`/projects/${projectId}/client-portal/tasks/${taskId}/comments`, data);
    return res.json();
  };
  const createClientPortalTaskCommentReply = async (
    projectId: string,
    taskId: string,
    commentId: string,
    data: ClientPortalCommentPayload,
  ): Promise<ResourceData> => {
    const res = await sendPostRequest(
      `/projects/${projectId}/client-portal/tasks/${taskId}/comments/${commentId}/replies`,
      data,
    );
    return res.json();
  };

  const updateClientPortalTaskComment = async (
    projectId: string,
    commentId: string,
    data: ClientPortalCommentPayload,
  ): Promise<ResourceData> => {
    const res = await sendPatchRequest(`/projects/${projectId}/client-portal/tasks/comments/${commentId}`, data);
    return res.json();
  };

  const getCreatePortalTaskValidation = () => Promise.resolve(createClientPortalTaskValidation);
  const getCancelTaskValidation = () => Promise.resolve(cancelTaskValidation);
  const getRejectUserValidation = () => Promise.resolve(rejectClientUsersValidation);

  return {
    ...baseCrudRequests,
    createClientPortalTask,
    updateClientPortalTask,
    cancelClientPortalTask,
    inviteClientUsers,
    getActiveUserAccessRequests,
    approveClientUsers,
    rejectClientUsers,
    createClientPortalTaskComment,
    createClientPortalTaskCommentReply,
    updateClientPortalTaskComment,
    getCreatePortalTaskValidation,
    getCancelTaskValidation,
    getActivePortalRequests,
    getRejectUserValidation,
  };
};

export default useClientPortalService;
